import React, {
  useEffect,
  useRef,
  useState,
  createContext,
  useContext,
  useMemo,
} from 'react';
import MicroModal from 'react-micro-modal';
import { TokenMetadata, ftGetTokenMetadata } from '../../services/ft-contract';
import { ArrowDownWhite, QuestionMark, TokenRisk } from '../icon';
import { isMobile } from '../../utils/device';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  TokenBalancesView,
  registerTokenAndExchange,
} from '../../services/token';
import { IoCloseOutline, FaSearch } from '../reactIcons';
import CommonBases from '../../components/tokens/CommonBases';
import { useTokensData, useGlobalWhitelistTokens } from '../../state/token';
import { toRealSymbol, getImageMark, isSuffix } from '../../utils/token';
import {
  NEARXIDS,
  TOKEN_BLACK_LIST,
  USDC_TOKEN_ID,
  USDT_TOKEN_ID,
} from '../../services/near';
import { SelectTokenCloseButton } from '../../components/icon/Common';
import _ from 'lodash';
import {
  GradientButton,
  ButtonTextWrapper,
} from '../../components/button/Button';
import { WalletContext } from '../../utils/wallets-integration';
import {
  nearMetadata,
  WRAP_NEAR_CONTRACT_ID,
  wnearMetadata,
} from '../../services/wrap-near';
import { useAllPoolsV2 } from '../../state/swapV3';
import { Images, Symbols } from '../../components/stableswap/CommonComp';
import { IconLeftV3 } from '../tokens/Icon';
import { PoolInfo } from '../../services/swapV3';
import getConfigV2 from '../../services/configV2';
import SelectTokenTable from './SelectTokenTable';
import CustomTooltip from '../customTooltip/customTooltip';
import { sort_tokens_by_base } from '../../services/dcl/dclDisplay';
import {
  useGraduatedZearfunTokens,
  useZearDexTokens,
} from '../../services/zearfun';
const configV2 = getConfigV2();

export const USER_COMMON_TOKEN_LIST = 'USER_COMMON_TOKEN_LIST3';

function sort(valueA: any, valueB: any) {
  if (typeof valueA === 'string' && typeof valueB === 'string') {
    return valueA.localeCompare(valueB);
  } else if (typeof valueA === 'number' && typeof valueB === 'number') {
    return valueA - valueB;
  } else {
    return valueA;
  }
}
export function SingleToken({ token }: { token: TokenMetadata }) {
  const is_native_token = configV2.NATIVE_TOKENS.includes(token?.id);
  const [showTooltip, setShowTooltip] = useState(false);
  const isTokenAtRisk = !!token.isRisk;
  const mark = getImageMark(token);
  return (
    <>
      {token.icon ? (
        <div
          className="relative flex-shrink-0 overflow-hidden mr-2 rounded-full"
          style={{ width: '30px', height: '30px' }}
        >
          <img
            src={token.icon}
            alt={toRealSymbol(token.symbol)}
            className="inline-block mr-2 border rounded-full border-black w-full h-full"
          />
          {mark ? (
            <div
              className="flex items-center justify-center bg-black bg-opacity-75 absolute bottom-0 left-0 right-0"
              style={{ height: '11px' }}
            >
              <span
                className="italic text-white text-sm gotham_bold relative"
                style={{
                  top: '-1px',
                  left: '-1px',
                  transform: 'scale(0.5, 0.5)',
                }}
              >
                {mark}
              </span>
            </div>
          ) : null}
        </div>
      ) : (
        <div
          className="inline-block mr-2 border rounded-full border-black relative overflow-hidden"
          style={{ width: '30px', height: '30px' }}
        >
          {mark ? (
            <div
              className="flex items-center justify-center bg-black bg-opacity-75 absolute bottom-0 left-0 right-0"
              style={{ height: '11px' }}
            >
              <span
                className="italic text-white text-sm gotham_bold relative"
                style={{
                  top: '-1px',
                  left: '-1px',
                  transform: 'scale(0.5, 0.5)',
                }}
              >
                {mark}
              </span>
            </div>
          ) : null}
        </div>
      )}
      <div className="flex flex-col justify-between">
        <div className={`flex items-center`}>
          <span
            className="text-sm text-white overflow-hidden whitespace-nowrap max-w-28 truncate"
            title={toRealSymbol(token.symbol)}
          >
            {toRealSymbol(token.symbol)}
          </span>
          {isTokenAtRisk && (
            <div
              className="ml-2 relative"
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
            >
              <span>
                <TokenRisk />
              </span>
              {showTooltip && (
                <div className="absolute z-50 -top-3 left-5 px-2 w-max py-1.5 border border-borderColor text-farmText text-xs rounded-md bg-cardBg">
                  Uncertified token, higher risk.
                </div>
              )}
            </div>
          )}
          {is_native_token ? (
            <span className="text-gradientFromHover bg-gradientFromHover bg-opacity-30 text-sm px-1 rounded-md ml-2 border border-gradientFromHover">
              Native
            </span>
          ) : null}
        </div>
        <span
          className="text-xs text-primaryText overflow-hidden whitespace-nowrap w-32 truncate"
          title={token.name}
        >
          {token.name}
        </span>
      </div>
    </>
  );
}

export const localTokens = createContext(null);
export default function SelectToken({
  tokens,
  selected,
  render,
  onSelect,
  standalone,
  placeholder,
  balances,
  tokenPriceList,
  forCross,
  customWidth,
  allowWNEAR,
  className,
  placeholderNode,
}: {
  tokens: TokenMetadata[];
  selected: string | React.ReactElement;
  standalone?: boolean;
  placeholder?: string;
  render?: (token: TokenMetadata) => string;
  onSelect?: (token: TokenMetadata) => void;
  onSearch?: (value: string) => void;
  balances?: TokenBalancesView;
  tokenPriceList?: Record<string, any>;
  forCross?: boolean;
  customWidth?: boolean;
  allowWNEAR?: boolean;
  className?: string;
  placeholderNode?: React.ReactNode;
}) {
  const graduatedTokens = useGraduatedZearfunTokens();
  const dexTokens = useZearDexTokens();
  const [baseTokens, setBaseTokens] = useState<TokenMetadata[]>(() => [
    {
      ...wnearMetadata,
      symbol: 'NEAR',
      name: 'NEAR',
      icon: nearMetadata.icon,
    },
    wnearMetadata,
  ]);
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      USDC_TOKEN_ID
        ? ftGetTokenMetadata(USDC_TOKEN_ID).catch(() => null)
        : Promise.resolve(null),
      USDT_TOKEN_ID
        ? ftGetTokenMetadata(USDT_TOKEN_ID).catch(() => null)
        : Promise.resolve(null),
    ]).then(([usdc, usdt]) => {
      if (cancelled) return;
      setBaseTokens([
        {
          ...wnearMetadata,
          symbol: 'NEAR',
          name: 'NEAR',
          icon: nearMetadata.icon,
        },
        wnearMetadata,
        ...(usdt?.id ? [usdt] : []),
        ...(usdc?.id ? [usdc] : []),
      ]);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  const listedTokens = useMemo(() => {
    const allow = new Set(
      [WRAP_NEAR_CONTRACT_ID, USDC_TOKEN_ID, USDT_TOKEN_ID].filter(Boolean)
    );
    dexTokens.forEach((token) => token?.id && allow.add(token.id));
    graduatedTokens.forEach((token) => token?.id && allow.add(token.id));
    const merged: TokenMetadata[] = [];
    const seen = new Set<string>();
    const push = (token?: TokenMetadata) => {
      if (!token?.id || !allow.has(token.id)) return;
      const key =
        token.id === WRAP_NEAR_CONTRACT_ID
          ? `${token.id}:${token.symbol}`
          : token.id;
      if (seen.has(key)) return;
      seen.add(key);
      merged.push(token);
    };
    (tokens || []).forEach(push);
    baseTokens.forEach(push);
    dexTokens.forEach(push);
    graduatedTokens.forEach(push);
    return merged;
  }, [tokens, graduatedTokens, dexTokens, baseTokens]);
  const [visible, setVisible] = useState(false);
  const [allListData, setAllListData] = useState<TokenMetadata[]>([]);
  const [listData, setListData] = useState<TokenMetadata[]>([]);
  const [listTknData, setListTknData] = useState<TokenMetadata[]>([]);
  const [listTknxData, setListTknxData] = useState<TokenMetadata[]>([]);
  const [listmemeCookingData, setListmemeCookingData] = useState<
    TokenMetadata[]
  >([]);
  const [searchResultslistData, setSearchResultsData] = useState<
    TokenMetadata[]
  >([]);
  const [currentSort, setSort] = useState<string>('down');
  const [sortBy, setSortBy] = useState<string>('near');
  const [showCommonBases, setShowCommonBases] = useState<boolean>(true);
  const [commonBasesTokens, setCommonBasesTokens] = useState([]);
  const [searchNoData, setSearchNoData] = useState(false);
  const [tknSearchNoData, setTknSearchNoData] = useState(false);
  const [addTokenLoading, setAddTokenLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [addTokenError, setAddTokenError] = useState(false);
  const { globalState } = useContext(WalletContext);
  const isSignedIn = globalState.isSignedIn;

  const dialogWidth = isMobile() ? '95%' : forCross ? '25%' : '420px';
  const dialogMinwidth = isMobile() ? 340 : 380;
  const dialogHeight = isMobile() ? '95%' : '57%';
  const dialogMinHeight = isMobile() ? '95%' : '540px';
  const intl = useIntl();
  const searchRef = useRef(null);
  const [activeTab, setActiveTab] = useState('Default');
  const [showTabs, setShowTabs] = useState(true);
  const { tokensData: allTokensData, loading: loadingAllTokensData } =
    useTokensData(listedTokens, balances, visible);
  const { tokensData, loading: loadingTokensData } = useTokensData(
    listedTokens.filter(
      (token) =>
        TOKEN_BLACK_LIST.indexOf(token.id) === -1 &&
        (!token.isRisk || (token.isRisk && token.isUserToken))
    ),
    balances,
    visible
  );
  const { tokensData: tknTokensData, loading: loadingTKNTokensData } =
    useTokensData(
      listedTokens.filter(
        (token) =>
          TOKEN_BLACK_LIST.indexOf(token.id) === -1 &&
          isSuffix(token.id, 'tkn') &&
          token.isRisk &&
          !token.isUserToken
      ),
      balances,
      visible
    );
  const { tokensData: tknxTokensData, loading: loadingTKNXTokensData } =
    useTokensData(
      listedTokens.filter(
        (token) =>
          TOKEN_BLACK_LIST.indexOf(token.id) === -1 &&
          isSuffix(token.id, 'tknx') &&
          token.isRisk &&
          !token.isUserToken
      ),
      balances,
      visible
    );
  const {
    tokensData: memeCookingTokensData,
    loading: loadingMemeCookingTokensData,
  } = useTokensData(
    listedTokens.filter(
      (token) =>
        TOKEN_BLACK_LIST.indexOf(token.id) === -1 &&
        isSuffix(token.id, 'meme-cooking') &&
        token.isRisk &&
        !token.isUserToken
    ),
    balances,
    visible
  );
  const sortTypes = useMemo(
    () => ({
      up: {
        class: 'sort-up',
        fn: (tokenA: any, tokenB: any) => sort(tokenA[sortBy], tokenB[sortBy]),
      },
      down: {
        class: 'sort-down',
        fn: (tokenA: any, tokenB: any) => sort(tokenB[sortBy], tokenA[sortBy]),
      },
      default: {
        class: 'sort',
        fn: (tokenA: any, tokenB: any) => tokenA,
      },
    }),
    [sortBy]
  );
  useEffect(() => {
    if (allTokensData?.length && !loadingAllTokensData) {
      const sortedData = [...allTokensData].sort(sortTypes[currentSort].fn);
      setAllListData(sortedData);
    }
  }, [allTokensData?.length, loadingAllTokensData]);
  useEffect(() => {
    if (searchResultslistData?.length) {
      searchResultslistData.sort(sortTypes[currentSort].fn);
      setSearchResultsData(searchResultslistData);
    }
  }, [searchResultslistData?.length, currentSort, allTokensData]);
  useEffect(() => {
    if (tokensData?.length && !loadingTokensData) {
      tokensData.sort(sortTypes[currentSort].fn);
      setListData(tokensData);
    }
  }, [tokensData?.length, loadingTokensData, currentSort]);
  useEffect(() => {
    if (tknTokensData?.length && !loadingTKNTokensData) {
      tknTokensData.sort(sortTypes[currentSort].fn);
      setListTknData(tknTokensData);
    }
  }, [tknTokensData?.length, loadingTKNTokensData, currentSort]);

  useEffect(() => {
    if (tknTokensData?.length && !loadingTKNXTokensData) {
      tknxTokensData.sort(sortTypes[currentSort].fn);
      setListTknxData(tknxTokensData);
    }
  }, [tknxTokensData?.length, loadingTKNXTokensData, currentSort]);
  useEffect(() => {
    if (memeCookingTokensData?.length && !loadingMemeCookingTokensData) {
      memeCookingTokensData.sort(sortTypes[currentSort].fn);
      setListmemeCookingData(memeCookingTokensData);
    }
  }, [
    memeCookingTokensData?.length,
    loadingMemeCookingTokensData,
    currentSort,
  ]);

  useEffect(() => {
    getLatestCommonBasesTokens();
  }, [tokensData]);

  const sortBySymbol = (tokenA: TokenMetadata, tokenB: TokenMetadata) => {
    if (+tokenA.near == 0 && +tokenB.near == 0) {
      const a_symbol = toRealSymbol(tokenA.symbol).toLocaleLowerCase();
      const b_symbol = toRealSymbol(tokenB.symbol).toLocaleLowerCase();
      return a_symbol.localeCompare(b_symbol);
    }
  };

  const onSortChange = (params: string) => {
    if (params === sortBy) {
      let nextSort;
      if (currentSort === 'down') nextSort = 'up';
      else if (currentSort === 'up') nextSort = 'down';
      setSort(nextSort);
    } else {
      setSort('up');
    }
    setSortBy(params);
  };

  const onSearch = (value: string) => {
    setAddTokenError(false);
    setAddTokenLoading(false);
    setSearchValue(value);
    setShowTabs(value.length === 0);
    setShowCommonBases(value.length === 0);
    // const result = tokensData.filter((token) => {
    //   const symbol = token?.symbol === 'NEAR' ? 'wNEAR' : token?.symbol;
    //   if (!symbol) return false;
    //   const condition1 = toRealSymbol(symbol)
    //     .toLocaleUpperCase()
    //     .includes(value.toLocaleUpperCase());
    //   const condition2 =
    //     token.id.toLocaleLowerCase() == value.toLocaleLowerCase();
    //   return condition1 || condition2;
    // });
    // const resultTkn = tknTokensData.filter((token) => {
    //   const symbol = token?.symbol === 'NEAR' ? 'wNEAR' : token?.symbol;
    //   if (!symbol) return false;
    //   const condition1 = toRealSymbol(symbol)
    //     .toLocaleUpperCase()
    //     .includes(value.toLocaleUpperCase());
    //   const condition2 =
    //     token.id.toLocaleLowerCase() == value.toLocaleLowerCase();
    //   return condition1 || condition2;
    // });
    // result.sort(sortBySymbol);
    // setListData(result);
    // setListTknData(resultTkn);
    const result = allListData
      .filter((token) => {
        const symbol = token?.symbol === 'NEAR' ? 'wNEAR' : token?.symbol;
        if (!symbol) return false;
        const symbolMatches = toRealSymbol(symbol)
          .toLocaleUpperCase()
          .includes(value.toLocaleUpperCase());
        const idMatches =
          token.id.toLocaleLowerCase() == value.toLocaleLowerCase();
        return symbolMatches || idMatches;
      })
      .sort(sortBySymbol);

    setSearchResultsData(result);

    if (!loadingTokensData && value.length > 0 && result.length == 0) {
      setSearchNoData(true);
    } else {
      setSearchNoData(false);
    }
    // if (!loadingTKNTokensData && value.length > 0 && resultTkn.length == 0) {
    //   setTknSearchNoData(true);
    // } else {
    //   setTknSearchNoData(false);
    // }
  };

  const debounceSearch = _.debounce(onSearch, 300);

  const handleClose = () => {
    const sortedData = [...tokensData].sort(sortTypes[currentSort].fn);
    if (tokensData.length > 0) {
      sortedData.sort(sortBySymbol);
      setListData(sortedData);
    }
    setVisible(false);
    setShowCommonBases(true);
    setSearchNoData(false);
    setAddTokenError(false);
    clear();
  };
  function getLatestCommonBasesTokens() {
    const local_user_list = getLatestCommonBasesTokenIds();
    const temp_tokens: TokenMetadata[] = [];
    local_user_list.forEach((id: string) => {
      const userToken = listedTokens.find((token: TokenMetadata) => {
        if (id == 'near') {
          if (token.id == WRAP_NEAR_CONTRACT_ID && token.symbol == 'NEAR')
            return true;
        } else if (id == WRAP_NEAR_CONTRACT_ID) {
          if (token.id == WRAP_NEAR_CONTRACT_ID && token.symbol == 'wNEAR')
            return true;
        } else {
          if (token.id == id) return true;
        }
      });
      if (userToken) {
        temp_tokens.push(userToken);
      }
    });
    setCommonBasesTokens(temp_tokens);
  }
  function getLatestCommonBasesTokenIds() {
    const cur_status = localStorage.getItem(USER_COMMON_TOKEN_LIST);
    if (!cur_status) {
      // On testnet the bridged and native stablecoin IDs are the same token.
      const init = _.uniq([
        'near',
        WRAP_NEAR_CONTRACT_ID,
        USDC_TOKEN_ID,
        USDT_TOKEN_ID,
      ]);
      localStorage.setItem(USER_COMMON_TOKEN_LIST, JSON.stringify(init));
    }
    const local_user_list_str =
      localStorage.getItem(USER_COMMON_TOKEN_LIST) || '[]';
    // Earlier builds stored the duplicated defaults.
    const local_user_list = _.uniq(JSON.parse(local_user_list_str));
    return local_user_list;
  }
  function addTokenSubmit() {
    setAddTokenError(false);
    setAddTokenLoading(true);
    registerTokenAndExchange(searchValue)
      .then()
      .catch((error) => {
        setAddTokenError(true);
        setAddTokenLoading(false);
      });
  }
  function clear() {
    setSearchValue('');
    searchRef.current.value = '';
    onSearch('');
  }
  if (!onSelect) {
    return (
      <button className="focus:outline-none p-1" type="button">
        {selected}
      </button>
    );
  }
  const TknTip = `
    <div class="text-navHighLightText text-xs text-left w-64 xsm:w-52">
    Created by any user on https://tkn.homes with the tkn.near suffix, poses high risks. Ref has not certified it. Exercise caution.
    </div>`;
  const TknxTip = `
    <div class="text-navHighLightText text-xs text-left w-64 xsm:w-52">
    Created by any user on https://tkn.homes with the tknx.near suffix, poses high risks. Ref has not certified it. Exercise caution.
    </div>`;
  const memeCookingTip = `
    <div class="text-navHighLightText text-xs text-left w-64 xsm:w-52">
    Created by any user on https://meme.cooking/create with the meme-cooking.near suffix, poses high risks. Ref has not certified it. Exercise caution.
    </div>`;
  return (
    <MicroModal
      open={visible}
      handleClose={handleClose}
      trigger={() => (
        <div
          className={`focus:outline-none my-auto flex-shrink-0  ${
            !customWidth ? (standalone ? 'w-full' : className || 'w-2/5') : ''
          }`}
          onClick={() => setVisible(true)}
        >
          {selected || placeholderNode || (
            <section
              className={`flex justify-between items-center px-3 py-2 ${
                standalone
                  ? 'bg-inputDarkBg text-white relative flex overflow-hidden rounded align-center my-2'
                  : ''
              }`}
            >
              <p
                className="text-lg text-gray-400 font-semibold leading-none"
                style={{ lineHeight: 'unset' }}
              >
                {placeholder ?? 'Select'}
              </p>
              <div className="pl-2">
                <ArrowDownWhite />
              </div>
            </section>
          )}
        </div>
      )}
      overrides={{
        Overlay: {
          style: {
            zIndex: 110,
            backgroundColor: 'rgba(0, 0, 0, 0.55)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
          },
        },
        Dialog: {
          style: {
            width: dialogWidth,
            minWidth: dialogMinwidth,
            borderRadius: 18,
            border: '1px solid rgba(255,255,255,0.08)',
            padding: '1.5rem 0',
            background: '#0E1426',
            height: dialogHeight,
            minHeight: dialogMinHeight,
            zIndex: 100,
            overflow: 'hidden',
            position: 'relative',
          },
        },
      }}
    >
      {() => (
        <section className="text-white">
          <div className="flex items-center justify-between pb-5 px-8 xsm:px-5 relative">
            <h2 className="text-center gotham_bold text-lg">
              <FormattedMessage
                id="select_token"
                defaultMessage="Select Token"
              />
            </h2>
            <IoCloseOutline
              onClick={() => handleClose()}
              className="absolute text-gray-400 text-2xl right-6 cursor-pointer"
            />
          </div>
          <div className="flex flex-col  mb-5 mx-6 xsm:mx-3">
            <div
              className="relative flex items-center h-11 text-gray-400 px-3"
              style={{
                background: '#0b1020',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 12,
              }}
            >
              <FaSearch
                className={`mr-2 ${
                  searchValue ? 'text-greenColor' : 'text-farmText'
                }`}
              />
              <input
                ref={searchRef}
                className={`text-base text-white outline-none rounded w-full py-2 pl-1 mr-6`}
                placeholder={intl.formatMessage({
                  id: 'search_name_or_address',
                })}
                onChange={(evt) => debounceSearch(evt.target.value)}
              />
              <SelectTokenCloseButton
                onClick={clear}
                className={`absolute right-3 cursor-pointer ${
                  searchValue ? '' : 'hidden'
                }`}
              ></SelectTokenCloseButton>
            </div>
            {addTokenError ? (
              <div className="text-redwarningColor text-sm mt-2">
                <FormattedMessage id="token_address_invalid"></FormattedMessage>
              </div>
            ) : null}
          </div>
          <div
            className="overflow-auto overflow-x-hidden absolute w-full"
            style={{ height: 'calc(100% - 150px)' }}
          >
            <localTokens.Provider
              value={{
                commonBasesTokens,
                getLatestCommonBasesTokens,
                getLatestCommonBasesTokenIds,
              }}
            >
              {showCommonBases && (
                <CommonBases
                  onClick={(token) => {
                    onSelect && onSelect(token);
                  }}
                  tokenPriceList={tokenPriceList}
                  allowWNEAR={allowWNEAR}
                  handleClose={handleClose}
                />
              )}
              {showTabs ? (
                <SelectTokenTable
                  className="mt-1"
                  sortBy={sortBy}
                  tokenPriceList={tokenPriceList}
                  currentSort={currentSort}
                  onSortChange={onSortChange}
                  tokens={listData}
                  onClick={(token) => {
                    if (token.id != NEARXIDS[0]) {
                      if (
                        !(
                          token.id == WRAP_NEAR_CONTRACT_ID &&
                          token.symbol == 'wNEAR' &&
                          !allowWNEAR
                        )
                      ) {
                        onSelect && onSelect(token);
                      }
                    }
                    handleClose();
                  }}
                  balances={balances}
                  forCross={forCross}
                  showRiskTokens={false}
                />
              ) : (
                <div className="">
                  <p className="mb-2 h-7 w-fit text-primaryText font-normal pb-9 pl-8 text-sm">
                    Search Result
                  </p>
                  <SelectTokenTable
                    sortBy={sortBy}
                    tokenPriceList={tokenPriceList}
                    currentSort={currentSort}
                    onSortChange={onSortChange}
                    tokens={searchResultslistData}
                    onClick={(token) => {
                      if (token.id != NEARXIDS[0]) {
                        if (
                          !(
                            token.id == WRAP_NEAR_CONTRACT_ID &&
                            token.symbol == 'wNEAR' &&
                            !allowWNEAR
                          )
                        ) {
                          onSelect && onSelect(token);
                        }
                      }
                      handleClose();
                    }}
                    balances={balances}
                    forCross={forCross}
                    showAllTokens={true}
                  />
                </div>
              )}
            </localTokens.Provider>
          </div>

          {searchNoData && activeTab === 'Default' ? (
            <div className="flex flex-col  items-center justify-center mt-32 relative z-10">
              <div className="text-sm text-farmText">
                <FormattedMessage id="no_token_found"></FormattedMessage>
              </div>
              {isSignedIn ? (
                <GradientButton
                  onClick={addTokenSubmit}
                  color="#fff"
                  loading={addTokenLoading}
                  className={`h-9 mt-5 px-6 xsm:px-3.5 text-center text-sm text-white focus:outline-none`}
                >
                  <ButtonTextWrapper
                    loading={addTokenLoading}
                    Text={() => (
                      <FormattedMessage
                        id="add_token"
                        defaultMessage="Add token"
                      />
                    )}
                  />
                </GradientButton>
              ) : null}
            </div>
          ) : null}
          {tknSearchNoData && activeTab === 'TKN' ? (
            <div className="flex flex-col  items-center justify-center mt-32 relative z-10">
              <div className="text-sm text-farmText">
                <FormattedMessage id="no_token_found"></FormattedMessage>
              </div>
            </div>
          ) : null}
          {tknSearchNoData && activeTab === 'TKNX' ? (
            <div className="flex flex-col  items-center justify-center mt-32 relative z-10">
              <div className="text-sm text-farmText">
                <FormattedMessage id="no_token_found"></FormattedMessage>
              </div>
            </div>
          ) : null}
        </section>
      )}
    </MicroModal>
  );
}

export function SelectTokenDCL({
  selectTokenIn,
  selectTokenOut,
  selectedToken,
  onSelect,
  selected,
  className,
  notNeedSortToken,
  limitOrder = false,
}: {
  selectTokenIn?: (token: TokenMetadata) => void;
  selectTokenOut?: (token: TokenMetadata) => void;
  onSelect?: (token: TokenMetadata) => void;
  selectedToken?: TokenMetadata;
  selected?: JSX.Element;
  className?: string;
  notNeedSortToken?: boolean;
  limitOrder?: boolean;
}) {
  const allPools = useAllPoolsV2(!limitOrder);

  const [hoverSelectToken, setHoverSelectToken] = useState<boolean>(false);

  const intl = useIntl();

  const mobileDevice = isMobile();

  const globalWhiteList = useGlobalWhitelistTokens();

  const displayPools = allPools?.reduce((poolsByPair, pool, index) => {
    const id = [pool.token_x, pool.token_y].sort().join('|');
    if (!poolsByPair[id]) {
      poolsByPair[id] = pool;
    }
    return poolsByPair;
  }, {} as Record<string, PoolInfo>);

  const handleSelect = (pool: PoolInfo) => {
    // select token in
    const { token_x_metadata, token_y_metadata } = pool;
    const tokens = notNeedSortToken
      ? [token_x_metadata, token_y_metadata]
      : sort_tokens_by_base([token_x_metadata, token_y_metadata]);

    if (!selectedToken) {
      selectTokenIn(tokens[0]);
      selectTokenOut(tokens[1]);

      return;
    }

    if (!!selectTokenOut) {
      if (selectedToken?.id === tokens[0].id) {
        selectTokenOut(tokens[1]);
      } else if (selectedToken?.id === tokens[1].id) {
        selectTokenOut(tokens[0]);
      } else {
        onSelect(tokens[0]);
        selectTokenOut(tokens[1]);
      }

      return;
    }

    if (!!selectTokenIn) {
      if (selectedToken?.id === tokens[0].id) {
        selectTokenIn(tokens[1]);
      } else if (selectedToken?.id === tokens[1].id) {
        selectTokenIn(tokens[0]);
      } else {
        onSelect(tokens[1]);
        selectTokenIn(tokens[0]);
      }
      return;
    }
  };

  const renderPools = useMemo(
    () =>
      Object.values(displayPools || {})?.filter((pool) => {
        const { token_x_metadata, token_y_metadata } = pool;
        return (
          !!globalWhiteList.find(
            (whitelistedToken) => whitelistedToken.id === token_x_metadata.id
          ) &&
          !!globalWhiteList.find(
            (whitelistedToken) => whitelistedToken.id === token_y_metadata.id
          )
        );
      }),
    [globalWhiteList]
  );

  const renderList = renderPools?.map((pool) => {
    const { token_x_metadata, token_y_metadata } = pool;
    const tokens = sort_tokens_by_base([token_x_metadata, token_y_metadata]);
    return (
      <div
        key={pool.pool_id}
        className="flex items-center text-sm xs:text-base min-w-max px-1.5 bg-opacity-90 py-3 rounded-lg hover:bg-dclSelectTokenHover cursor-pointer"
        onClick={() => {
          handleSelect(pool);
          setHoverSelectToken(false);
        }}
      >
        <Images tokens={tokens} size="5" className="mr-2 ml-1" />

        <Symbols tokens={tokens} separator="-" />
      </div>
    );
  });

  // fetch all dcl pools

  useEffect(() => {
    if (hoverSelectToken && mobileDevice) {
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.documentElement.style.overflow = 'auto';
    }
  }, [hoverSelectToken]);

  return (
    <div
      className="outline-none relative my-auto flex-shrink-0"
      onMouseLeave={() => {
        if (!mobileDevice) {
          setHoverSelectToken(false);
        }
      }}
      onBlur={() => {
        if (mobileDevice) {
          setHoverSelectToken(false);
        }
      }}
    >
      {(selected || selectedToken) && (
        <div
          className="flex items-center relative justify-end font-semibold"
          onMouseEnter={() => {
            if (!mobileDevice) {
              setHoverSelectToken(true);
            }
          }}
          onClick={() => {
            if (mobileDevice) {
              setHoverSelectToken(!hoverSelectToken);
            }
          }}
          style={{
            zIndex: !!selectTokenOut ? 80 : 70,
          }}
        >
          {selected || (
            <IconLeftV3
              size={'7'}
              token={selectedToken}
              hover={hoverSelectToken}
              className={'p-1'}
            />
          )}
        </div>
      )}

      {hoverSelectToken && renderList?.length > 0 && (
        <div
          className={`${
            className ||
            'pt-2  absolute top-8 outline-none xs:text-white xs:font-bold xs:fixed xs:bottom-0 xs:w-full  right-0'
          }    `}
          onMouseLeave={() => {
            if (!mobileDevice) {
              setHoverSelectToken(false);
            }
          }}
          onBlur={() => {
            if (mobileDevice) {
              setHoverSelectToken(false);
            }
          }}
          style={{
            zIndex: mobileDevice ? 80 : !!selectTokenOut ? 80 : 75,
          }}
        >
          {mobileDevice && (
            <div
              className="fixed w-screen h-screen top-0"
              style={{
                zIndex: 150,
                background: 'rgba(0, 19, 32, 0.8)',
                position: 'fixed',
              }}
              onClick={() => {
                setHoverSelectToken(false);
              }}
            ></div>
          )}
          <div
            className="overflow-auto xs:absolute xs:w-full xs:bottom-0 xs:pb-8 px-2 py-3 "
            style={{
              zIndex: mobileDevice ? 300 : '',
              maxHeight: mobileDevice ? `${48 * 10 + 78}px` : '',
              minHeight: mobileDevice ? `${48 * 5 + 78}px` : '',
              background: '#0E1426',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 18,
            }}
          >
            <div className="text-sm text-primaryText xs:text-white xs:text-base  ml-1.5   pb-2">
              {intl.formatMessage({
                id: 'instrument',
                defaultMessage: 'Instrument',
              })}
            </div>
            {renderList}
          </div>
        </div>
      )}
    </div>
  );
}
